import axios from 'axios'
import store from '@/store'
import CSRF from '@/services/csrf'

// DRF TokenAuthentication's 401 detail when the token's user has been deactivated.
const ACCOUNT_DEACTIVATED_DETAIL = 'User inactive or deleted.'
const ACCOUNT_DEACTIVATED_MESSAGE =
  'Your account has been deactivated. Accounts are deactivated after a period of inactivity or by an ' +
  'administrator. Please contact your field director or admin to reactivate it.'

/**
 * Axios API client service with automatic token handling
 * - Sets baseURL to /api/
 * - Automatically includes CSRF tokens
 * - Adds Authorization header when logged in
 * - Handles token invalidation (null token or 401 responses)
 */
class ApiService {
  static session
  static init

  constructor() {
    let baseURL = `${window.location.protocol}//${window.location.host}/api`
    console.debug(`API Service initialized for ${baseURL}`)

    ApiService.session = axios.create({
      baseURL,
      headers: {
        ...CSRF.getHeaders(),
      },
    })

    // Request interceptor
    ApiService.session.interceptors.request.use(
      async (config) => {
        if (store.getters.isLoggedIn) {
          const token = store.getters.token
          if (!token) {
            await ApiService.handleInvalidToken()
            return Promise.reject(new Error('Authentication token missing'))
          }
          config.headers['Authorization'] = `Token ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    // Response interceptor
    ApiService.session.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response && error.response.status === 401) {
          await ApiService.handleInvalidToken(error.response.data?.detail)
        }
        return Promise.reject(error)
      },
    )
  }

  /**
   * Handle invalid token scenarios
   * - Clear user data from store
   * - Redirect to home page
   */
  static async handleInvalidToken(detail) {
    try {
      const [{ default: IdleTimer }, { default: router }] = await Promise.all([
        import('@/services/idleTimer'),
        import('@/router'),
      ])
      IdleTimer.stop()
      if (detail === ACCOUNT_DEACTIVATED_DETAIL && store.getters.isLoggedIn) {
        // Deactivated while logged in: explain instead of silently dropping them on the home page.
        const { useToast } = await import('vue-toastification')
        useToast().error(ACCOUNT_DEACTIVATED_MESSAGE, { timeout: 10000 })
      }
      await store.dispatch('setUser', null)
      if (router.currentRoute.value.name !== 'Home') {
        router.push({ name: 'Home' })
      }
    } catch (err) {
      console.error('Error during token invalidation handling:', err)
    }
  }

  /**
   * Singleton instance accessor
   */
  static get instance() {
    if (!ApiService.init) {
      new ApiService()
      ApiService.init = true
    }
    return ApiService.session
  }
}

// Export the singleton instance as default
export default ApiService.instance

// Export the class for extension if needed
export { ApiService }
